import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import EmptyState from "@/components/EmptyState";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Users as UsersIcon, Copy, ExternalLink, Star, CheckCircle2, XCircle, Clock,
  Sparkles, Trophy, User as UserIcon, Mail, Phone, MapPin, Music4, Search, Send, Trash2,
  Layers, Plus, GripVertical, ArrowRight, CalendarClock, ShieldCheck, Share2, UserCheck, BarChart3
} from "lucide-react";
import { fmtDate, fmtTime } from "@/lib/format";
import { Link } from "react-router-dom";
import AuditionSlotsDialog from "@/pages/AuditionSlotsDialog";
import JudgingLinkDialog from "@/pages/JudgingLinkDialog";
import VideoEmbed from "@/components/VideoEmbed";
import VideoNotes from "@/components/VideoNotes";
import AuditionFieldsDialog from "@/pages/AuditionFieldsDialog";
import ShareScoresDialog from "@/components/ShareScoresDialog";
import AuditionAnalytics from "@/components/AuditionAnalytics";
import AuditionResponsesPane from "@/components/AuditionResponsesPane";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

const STATUS_META = {
  pending: { label: "Pending", chip: "text-amber-300 bg-amber-500/10 border-amber-500/30", Icon: Clock },
  cast: { label: "Cast", chip: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30", Icon: CheckCircle2 },
  reject: { label: "Do not cast", chip: "text-red-300 bg-red-500/10 border-red-500/30", Icon: XCircle },
};

function ScoreBar({ label, value }) {
  const pct = value ? Math.round((value / 10) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-[11px] text-zinc-400 mb-1"><span>{label}</span><span className="text-gold font-mono">{value ?? "—"}</span></div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gold" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

/** Wraps a single virtual-audition video with its own seek state + notes. */
function VirtualVideoBlock({ label, url, videoKey, auditionId }) {
  const [seekSec, setSeekSec] = useState(0);
  return (
    <div className="space-y-1" data-testid={`virtual-video-${videoKey}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-400">{label}</div>
      <VideoEmbed url={url} label={label} seekSec={seekSec} />
      {url && auditionId && (
        <VideoNotes auditionId={auditionId} videoKey={videoKey} onSeek={(sec) => setSeekSec(sec)} />
      )}
    </div>
  );
}

function AuditionCard({ a, onOpen }) {
  const meta = STATUS_META[a.status] || STATUS_META.pending;
  return (
    <button
      onClick={() => onOpen(a)}
      data-testid={`audition-card-${a.id}`}
      className="text-left group rounded-lg border border-white/10 bg-[#121212] hover:border-gold/40 hover:-translate-y-0.5 transition-all overflow-hidden"
    >
      <div className="aspect-[4/5] bg-[#0A0A0A] overflow-hidden relative">
        {a.headshot_url ? (
          <img src={a.headshot_url} alt={a.name} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
        ) : (
          <div className="w-full h-full grid place-items-center text-zinc-700"><UserIcon className="w-12 h-12" /></div>
        )}
        <div className={`absolute top-2 left-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${meta.chip}`}>{meta.label}</div>
        {a.round_name && (
          <div className="absolute top-2 right-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-black/60 text-zinc-200 border border-white/15 inline-flex items-center gap-1">
            <Layers className="w-3 h-3" /> {a.round_name}
          </div>
        )}
        {a.overall_avg != null && (
          <div className="absolute bottom-2 right-2 text-[11px] font-mono px-2 py-1 rounded bg-black/70 text-gold border border-gold/30 flex items-center gap-1">
            <Star className="w-3 h-3 fill-current" />{a.overall_avg}
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="font-display truncate">{a.name}</div>
        <div className="text-[11px] text-zinc-500 truncate">{a.email}</div>
        <div className="mt-2 flex flex-wrap gap-1">
          {(a.interested_roles || []).slice(0, 2).map((r) => (
            <span key={r} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-300 border border-white/10 truncate max-w-[9rem]">{r}</span>
          ))}
          {(a.interested_roles || []).length > 2 && <span className="text-[10px] text-zinc-500">+{a.interested_roles.length - 2}</span>}
        </div>
        {a.status === "cast" && a.cast_character && (
          <div className="mt-2 text-[10px] uppercase tracking-widest text-emerald-400 truncate">→ {a.cast_character}</div>
        )}
      </div>
    </button>
  );
}

function DetailPanel({ audition, onClose, refresh, characters, rounds, slots = [], fields = [] }) {
  const { user, production } = useAuth();
  // Same widened gate as the outer Auditions page — production_staff needs
  // Cast / Do-not-cast, character assignment, round moves, and slot assign
  // on individual auditioner cards.
  const isAdmin = user.role === "org_admin" || user.role === "production_staff" || user.role === "super_admin";
  const [detail, setDetail] = useState(null);
  const [scoreForm, setScoreForm] = useState({ acting_score: null, vocal_score: null, dance_score: null, comment: "", suggested_roles: [] });
  const [castDialog, setCastDialog] = useState(false);
  const [castChoices, setCastChoices] = useState([]); // multi-select array of role names
  const [castCustom, setCastCustom] = useState("");
  const [castSearch, setCastSearch] = useState("");
  const [castGroupChoices, setCastGroupChoices] = useState([]); // Iter 74 — ensemble groups picked at cast time
  const [castGroups, setCastGroups] = useState([]);              // list of groups fetched once for the picker
  const [suggestQuery, setSuggestQuery] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get(`/auditions/${audition.id}`);
    setDetail(data);
    setScoreForm({
      acting_score: data.my_score?.acting_score ?? null,
      vocal_score: data.my_score?.vocal_score ?? null,
      dance_score: data.my_score?.dance_score ?? null,
      comment: data.my_score?.comment || "",
      suggested_roles: data.my_score?.suggested_roles || [],
    });
    // Prime the group multi-select from what the director has stashed on
    // the audition — this lets them re-open the dialog and see prior picks.
    setCastGroupChoices(data.cast_group_ids || []);
  }, [audition.id]);

  useEffect(() => { load(); }, [load]);
  // Fetch ensemble groups once when an admin opens the panel. Cast users
  // never see the cast dialog so the extra call is scoped correctly.
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/character-groups").then((r) => setCastGroups(r.data || [])).catch(() => {});
  }, [isAdmin]);

  const finalized = detail?.finalized;

  const saveScore = async () => {
    try {
      await api.post(`/auditions/${audition.id}/score`, scoreForm);
      toast.success("Score saved");
      await load();
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save score"); }
  };

  const setDecision = async (decision, cast_characters = []) => {
    try {
      // Iter 74 — pass through selected ensemble groups so they get
      // applied on finalize (adds the freshly-provisioned user to each
      // group's member list).
      const body = { decision, cast_characters };
      if (decision === "cast") body.cast_group_ids = castGroupChoices;
      await api.patch(`/auditions/${audition.id}/decision`, body);
      const label =
        decision === "cast"     ? `Cast as ${cast_characters.join(" / ") || "—"}${castGroupChoices.length ? ` · ${castGroupChoices.length} group${castGroupChoices.length === 1 ? "" : "s"}` : ""}` :
        decision === "reject"   ? "Marked as Do Not Cast" :
        decision === "no_show"  ? "Marked as No Show — hidden from active list" :
                                  "Reset to pending";
      toast.success(label);
      await load();
      refresh();
      setCastDialog(false);
      setCastChoices([]); setCastCustom(""); setCastSearch("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update"); }
  };

  const remove = async () => {
    if (!confirm(`Delete ${audition.name}'s audition? This cannot be undone.`)) return;
    try {
      await api.delete(`/auditions/${audition.id}`);
      toast.success("Deleted");
      onClose(); refresh();
    } catch { toast.error("Could not delete"); }
  };

  const moveToRound = async (round_id) => {
    try {
      await api.patch(`/auditions/${audition.id}/round`, { round_id: round_id || null });
      const target = rounds.find((r) => r.id === round_id);
      toast.success(target ? `Moved to "${target.name}"` : "Round cleared");
      await load(); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not move"); }
  };

  const assignSlot = async (slot_id) => {
    try {
      await api.patch(`/auditions/${audition.id}/slot`, { slot_id: slot_id || null });
      toast.success(slot_id ? "Slot assigned" : "Slot cleared");
      await load(); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not assign slot"); }
  };

  const addSuggested = () => {
    const v = suggestQuery.trim();
    if (!v) return;
    if (!scoreForm.suggested_roles.includes(v))
      setScoreForm((s) => ({ ...s, suggested_roles: [...s.suggested_roles, v] }));
    setSuggestQuery("");
  };
  const rmSuggested = (v) => setScoreForm((s) => ({ ...s, suggested_roles: s.suggested_roles.filter((x) => x !== v) }));

  if (!detail) {
    return (
      <div className="fixed inset-0 z-40 grid place-items-center bg-black/70" onClick={onClose}>
        <div className="text-sm text-zinc-400">Loading…</div>
      </div>
    );
  }

  const meta = STATUS_META[detail.status] || STATUS_META.pending;

  // Only close the panel when the click lands *directly* on the backdrop.
  // Without this guard, clicks inside portaled children (like the Cast
  // dialog's character list) bubble up through the React tree to this
  // handler and unexpectedly dismiss the panel — the "director kicked off
  // the screen" bug.
  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };
  return (
    <div className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm overflow-y-auto" onClick={onBackdrop}>
      <div className="min-h-full py-6 px-4 md:px-8 flex items-start justify-center" onClick={(e) => e.stopPropagation()}>
        <div className="w-full max-w-4xl bg-[#0F0F0F] border border-white/10 rounded-lg overflow-hidden animate-fade-up" data-testid="audition-detail">
          {/* Cover banner */}
          <div className="grid grid-cols-1 md:grid-cols-3">
            <div className="md:col-span-1 bg-[#0A0A0A] aspect-square md:aspect-auto overflow-hidden relative">
              {detail.headshot_url ? (
                <img src={detail.headshot_url} alt={detail.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full grid place-items-center text-zinc-700"><UserIcon className="w-20 h-20" /></div>
              )}
            </div>
            <div className="md:col-span-2 p-6 md:p-8 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">{production?.title}</div>
                  <h2 className="font-display text-3xl md:text-4xl leading-tight truncate">{detail.name}</h2>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-zinc-400">
                    <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{detail.email}</span>
                    {detail.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{detail.phone}</span>}
                    {detail.age && <span>Age {detail.age}</span>}
                    {detail.height && <span>· {detail.height}</span>}
                  </div>
                </div>
                <button data-testid="close-detail" onClick={onClose} className="text-zinc-500 hover:text-white text-sm">✕</button>
              </div>

              <div className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-2 py-1 rounded border ${meta.chip}`}>
                <meta.Icon className="w-3.5 h-3.5" />
                {meta.label}{detail.status === "cast" && detail.cast_character ? ` — ${detail.cast_character}` : ""}
                {finalized && <span className="ml-2 text-[9px] opacity-70">FINALIZED</span>}
              </div>
              {detail.round_name && (
                <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400 border border-white/10 rounded px-2 py-1 ml-2">
                  <Layers className="w-3 h-3" /> Round: <span className="text-white">{detail.round_name}</span>
                </div>
              )}
              {(() => {
                const s = slots.find((x) => x.id === detail.slot_id);
                if (!s) return null;
                return (
                  <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400 border border-white/10 rounded px-2 py-1 ml-2">
                    <Clock className="w-3 h-3" /> Slot: <span className="text-white">{fmtDate(s.date)} · {fmtTime(s.start_time)}</span>
                  </div>
                );
              })()}

              {/* Overall scores */}
              <div className="grid grid-cols-3 gap-3">
                <ScoreBar label="Acting" value={detail.acting_avg} />
                <ScoreBar label="Vocal" value={detail.vocal_avg} />
                <ScoreBar label="Dance" value={detail.dance_avg} />
              </div>

              {/* Virtual audition videos — only visible when the auditioner
                  ticked the virtual box on their signup form. */}
              {detail.is_virtual && (
                <div className="rounded-lg border border-gold/30 bg-gold/5 p-3 space-y-3" data-testid="virtual-videos-block">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-gold flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Virtual audition
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[
                      { label: "Acting", url: detail.video_acting_url, key: "acting" },
                      { label: "Vocal",  url: detail.video_vocal_url,  key: "vocal" },
                      { label: "Dance",  url: detail.video_dance_url,  key: "dance" },
                    ].map(({ label, url, key }) => (
                      <VirtualVideoBlock key={key} label={label} url={url} videoKey={key} auditionId={detail.id} />
                    ))}
                  </div>
                </div>
              )}

              <div className="text-[11px] text-zinc-500">
                {detail.score_count || 0} adjudicator score{(detail.score_count || 0) === 1 ? "" : "s"} · Submitted {fmtDate(detail.submitted_at?.slice(0, 10))}
              </div>

              {/* Admin decision */}
              {isAdmin && !finalized && (
                <div className="space-y-2 pt-2 border-t border-white/10">
                  <div className="flex flex-wrap gap-2 items-center">
                    <button data-testid="btn-cast" onClick={() => setCastDialog((v) => !v)} style={goldBtn} className="h-9 px-3 rounded font-semibold text-sm inline-flex items-center gap-1.5 hover:brightness-110">
                      <CheckCircle2 className="w-4 h-4" /> {castDialog ? "Close casting" : "Cast"}
                    </button>
                    <button data-testid="btn-reject" onClick={() => setDecision("reject")} className="h-9 px-3 rounded border border-red-500/40 text-red-300 text-sm inline-flex items-center gap-1.5 hover:bg-red-500/10">
                      <XCircle className="w-4 h-4" /> Do not cast
                    </button>
                    <button data-testid="btn-no-show" onClick={() => setDecision("no_show")} className="h-9 px-3 rounded border border-zinc-500/40 text-zinc-300 text-sm inline-flex items-center gap-1.5 hover:bg-white/5" title="Hide from active list — no email sent, no account created">
                      <XCircle className="w-4 h-4" /> No show
                    </button>
                    {detail.status !== "pending" && (
                      <button onClick={() => setDecision("pending")} className="h-9 px-3 rounded border border-white/15 text-zinc-300 text-sm hover:bg-white/5">Reset to pending</button>
                    )}
                    <button onClick={remove} className="h-9 px-3 rounded border border-white/10 text-zinc-500 hover:text-red-400 text-sm ml-auto inline-flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                  </div>

                  {/* Inline casting panel — replaces the previous modal Dialog
                      so character clicks stay inside the DetailPanel React
                      tree. No portals means no bubbled-onClose surprises. */}
                  {castDialog && (
                    <div className="rounded-lg border border-gold/30 bg-gold/5 p-4 space-y-3" data-testid="cast-dialog">
                      <div className="flex items-center justify-between">
                        <Label className="text-[10px] uppercase tracking-[0.24em] text-gold">
                          Assign character{castChoices.length > 1 ? "s" : ""} for {detail.name}
                        </Label>
                        {castChoices.length > 0 && (
                          <span data-testid="cast-selected-count" className="text-[10px] uppercase tracking-widest text-gold">
                            {castChoices.length} selected
                          </span>
                        )}
                      </div>
                      {castChoices.length > 0 && (
                        <div className="flex flex-wrap gap-1.5" data-testid="cast-selected-pills">
                          {castChoices.map((name) => (
                            <button
                              key={name}
                              type="button"
                              onClick={() => setCastChoices((cur) => cur.filter((n) => n !== name))}
                              className="text-[11px] px-2 py-1 rounded-full bg-gold/15 border border-gold/40 text-gold inline-flex items-center gap-1 hover:bg-gold/25"
                            >
                              {name} <span className="text-gold/70">×</span>
                            </button>
                          ))}
                        </div>
                      )}
                      <Input
                        data-testid="cast-character-search"
                        value={castSearch}
                        onChange={(e) => setCastSearch(e.target.value)}
                        placeholder={`Search ${characters.length} characters — tap to add multiple`}
                        className="bg-[#0A0A0A] border-white/10 h-9"
                      />
                      <div
                        role="listbox"
                        aria-multiselectable="true"
                        aria-label="Available characters"
                        data-testid="cast-character-list"
                        className="max-h-56 overflow-y-auto rounded-md border border-white/10 bg-[#0A0A0A] divide-y divide-white/5"
                      >
                        {characters
                          .filter((c) => !castSearch.trim() || c.name.toLowerCase().includes(castSearch.toLowerCase()))
                          .map((c) => {
                            const active = castChoices.includes(c.name);
                            return (
                              <button
                                key={c.id}
                                type="button"
                                role="option"
                                aria-selected={active}
                                data-testid={`cast-char-${c.id}`}
                                onClick={() =>
                                  setCastChoices((cur) =>
                                    cur.includes(c.name) ? cur.filter((n) => n !== c.name) : [...cur, c.name]
                                  )
                                }
                                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-white/5 focus:bg-white/5 focus:outline-none ${
                                  active ? "bg-gold/10 text-gold" : "text-zinc-200"
                                }`}
                              >
                                <span className="flex items-center gap-2 truncate">
                                  <span className={`w-3.5 h-3.5 rounded border ${active ? "bg-gold border-gold" : "border-white/25"} inline-flex items-center justify-center shrink-0`}>
                                    {active && <span className="text-black text-[9px] leading-none">✓</span>}
                                  </span>
                                  <span className="truncate">{c.name}</span>
                                </span>
                                {c.voice_type && <span className="text-[10px] text-zinc-500 uppercase tracking-widest ml-3 shrink-0">{c.voice_type}</span>}
                              </button>
                            );
                          })}
                        {characters.filter((c) => !castSearch.trim() || c.name.toLowerCase().includes(castSearch.toLowerCase())).length === 0 && (
                          <div className="px-3 py-4 text-xs text-zinc-500 text-center">No matches — add a custom role below.</div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          data-testid="cast-custom-input"
                          value={castCustom}
                          onChange={(e) => setCastCustom(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && castCustom.trim() && !castChoices.includes(castCustom.trim())) {
                              e.preventDefault();
                              setCastChoices((cur) => [...cur, castCustom.trim()]);
                              setCastCustom("");
                            }
                          }}
                          className="bg-[#0A0A0A] border-white/10 h-9"
                          placeholder="Custom role (Ensemble, Swing, …)"
                        />
                        <button
                          type="button"
                          data-testid="cast-custom-add"
                          disabled={!castCustom.trim() || castChoices.includes(castCustom.trim())}
                          onClick={() => {
                            setCastChoices((cur) => [...cur, castCustom.trim()]);
                            setCastCustom("");
                          }}
                          className="h-9 px-3 rounded border border-white/15 text-sm text-zinc-200 hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Add
                        </button>
                      </div>
                      <div className="text-[11px] text-zinc-500">Tap each role to add it — the actor becomes one production member linked to every character on finalize.</div>

                      {/* Iter 74 — optional ensemble group assignment.
                          Applied on finalize (when the user is provisioned).
                          Groups can also be edited later via Company Setup. */}
                      {castGroups.length > 0 && (
                        <div className="rounded-md border border-white/10 bg-[#0A0A0A] p-3 space-y-2" data-testid="cast-group-picker">
                          <div className="flex items-center justify-between">
                            <Label className="text-[10px] uppercase tracking-[0.24em] text-zinc-400">Add to ensemble group(s) <span className="text-zinc-600 normal-case tracking-normal">— optional</span></Label>
                            {castGroupChoices.length > 0 && (
                              <span data-testid="cast-group-selected-count" className="text-[10px] uppercase tracking-widest text-gold">
                                {castGroupChoices.length} selected
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {castGroups.map((g) => {
                              const active = castGroupChoices.includes(g.id);
                              return (
                                <button
                                  key={g.id}
                                  type="button"
                                  data-testid={`cast-group-toggle-${g.id}`}
                                  onClick={() =>
                                    setCastGroupChoices((cur) =>
                                      cur.includes(g.id) ? cur.filter((x) => x !== g.id) : [...cur, g.id]
                                    )
                                  }
                                  className={`text-[11px] px-2 py-1 rounded-full border inline-flex items-center gap-1 ${
                                    active
                                      ? "bg-gold/20 border-gold/60 text-gold"
                                      : "border-white/15 text-zinc-300 hover:bg-white/5"
                                  }`}
                                >
                                  {g.parent_group_id ? "↳ " : ""}{g.name}
                                  <span className="text-[9px] text-zinc-500">{g.total_user_count}</span>
                                </button>
                              );
                            })}
                          </div>
                          <div className="text-[10px] text-zinc-500">Applied when auditions are finalized. Editable anytime under People → Ensembles.</div>
                        </div>
                      )}

                      <div className="flex items-center justify-end gap-2 pt-1 border-t border-white/5">
                        <button
                          type="button"
                          onClick={() => { setCastDialog(false); setCastChoices([]); setCastCustom(""); setCastSearch(""); }}
                          className="h-9 px-3 rounded border border-white/15 text-zinc-300 hover:bg-white/5 text-sm"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          data-testid="confirm-cast-btn"
                          disabled={castChoices.length === 0}
                          onClick={() => setDecision("cast", castChoices)}
                          style={goldBtn}
                          className="h-9 px-3 rounded font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Confirm cast{castChoices.length > 1 ? ` (${castChoices.length})` : ""}
                        </button>
                      </div>
                    </div>
                  )}

                  {rounds.length > 0 && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <Layers className="w-3 h-3 text-zinc-500" />
                      <span className="text-zinc-500 uppercase tracking-widest">Move to round</span>
                      <Select value={detail.round_id || "__none__"} onValueChange={(v) => moveToRound(v === "__none__" ? "" : v)}>
                        <SelectTrigger data-testid="move-round-select" className="bg-[#0A0A0A] border-white/10 h-8 w-[220px] text-xs"><SelectValue placeholder="Choose round" /></SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/10 text-white">
                          {rounds.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                          <SelectItem value="__none__">— No round —</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {slots.length > 0 && (
                    <div className="flex items-center gap-2 text-[11px]">
                      <CalendarClock className="w-3 h-3 text-zinc-500" />
                      <span className="text-zinc-500 uppercase tracking-widest">Assign slot</span>
                      <Select value={detail.slot_id || "__none__"} onValueChange={(v) => assignSlot(v === "__none__" ? "" : v)}>
                        <SelectTrigger data-testid="assign-slot-select" className="bg-[#0A0A0A] border-white/10 h-8 w-[280px] text-xs"><SelectValue placeholder="Choose slot" /></SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/10 text-white max-h-72">
                          <SelectItem value="__none__">— No slot —</SelectItem>
                          {slots.map((s) => {
                            const full = (s.filled || 0) >= (s.capacity || 1) && s.id !== detail.slot_id;
                            return (
                              <SelectItem key={s.id} value={s.id} disabled={full}>
                                {fmtDate(s.date)} · {fmtTime(s.start_time)}–{fmtTime(s.end_time)} {full ? "(full)" : `(${s.filled}/${s.capacity})`}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 md:p-8 border-t border-white/10">
            {/* Left column – questions */}
            <div className="md:col-span-2 space-y-5 text-sm">
              <Section title="About">
                <Field label="Vocal range" value={detail.vocal_range} />
                <Field label="Dance experience" value={detail.dance_experience} />
                <Field label="Special skills" value={detail.special_skills} />
                <Field label="Previous experience" value={detail.previous_experience} multiline />
              </Section>

              <Section title="Availability">
                <Field label="From" value={detail.availability_start} />
                <Field label="Until" value={detail.availability_end} />
              </Section>

              {detail.conflicts?.length > 0 && (
                <Section title={`Known conflicts (${detail.conflicts.length})`}>
                  <ul className="space-y-1">
                    {detail.conflicts.map((c, i) => (
                      <li key={i} className="text-zinc-300">
                        <span className="text-zinc-500">{fmtDate(c.date)}</span>{c.start_time && ` · ${fmtTime(c.start_time)}–${c.end_time ? fmtTime(c.end_time) : "?"}`}
                        {c.reason && <span className="text-zinc-500"> — {c.reason}</span>}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {detail.interested_roles?.length > 0 && (
                <Section title="Interested in">
                  <div className="flex flex-wrap gap-1.5">
                    {detail.interested_roles.map((r) => (
                      <span key={r} className="text-[11px] px-2 py-1 rounded border border-gold/30 bg-gold/5 text-gold">{r}</span>
                    ))}
                  </div>
                </Section>
              )}

              {(detail.emergency_contact_name || detail.emergency_contact_phone) && (
                <Section title="Emergency contact">
                  <Field label="Name" value={detail.emergency_contact_name} />
                  <Field label="Phone" value={detail.emergency_contact_phone} />
                </Section>
              )}

              {fields.length > 0 && detail.custom_answers && Object.keys(detail.custom_answers).length > 0 && (
                <Section title="Additional info">
                  {fields.map((f) => {
                    const v = detail.custom_answers[f.id];
                    if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) return null;
                    let display;
                    if (Array.isArray(v)) display = v.join(", ");
                    else if (typeof v === "object" && v.filename) display = <a href={v.signed_url || `${process.env.REACT_APP_BACKEND_URL}/api/auditions/${detail.id}/custom-file/${f.id}`} target="_blank" rel="noreferrer" className="text-gold underline">{v.filename}</a>;
                    else display = String(v);
                    return <Field key={f.id} label={f.label} value={display} />;
                  })}
                </Section>
              )}

              {/* Feed of adjudicator scores */}
              {detail.scores?.length > 0 && (
                <Section title="Adjudicator notes">
                  <ul className="space-y-3">
                    {detail.scores.map((s) => (
                      <li key={s.id} className="rounded border border-white/10 bg-[#0A0A0A] p-3">
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium">
                            {s.staff_name} <span className="text-[10px] uppercase tracking-widest text-zinc-500 ml-1">{s.staff_role}</span>
                            {s.round_name && <span className="text-[10px] uppercase tracking-widest text-gold border border-gold/30 rounded px-1.5 py-0.5 ml-2">{s.round_name}</span>}
                          </div>
                          <div className="text-[11px] font-mono text-gold flex items-center gap-2">
                            <span>A {s.acting_score ?? "—"}</span>
                            <span>V {s.vocal_score ?? "—"}</span>
                            <span>D {s.dance_score ?? "—"}</span>
                          </div>
                        </div>
                        {s.comment && <div className="text-zinc-300 mt-1 whitespace-pre-wrap">{s.comment}</div>}
                        {s.suggested_roles?.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {s.suggested_roles.map((r) => <span key={r} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-300 border border-white/10">Suggests: {r}</span>)}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
            </div>

            {/* Right column – my score */}
            <div className="space-y-4">
              <div className="rounded-lg border border-gold/30 bg-gold/5 p-4">
                <div className="text-[10px] uppercase tracking-widest text-gold flex items-center gap-1"><Sparkles className="w-3 h-3" /> Your evaluation</div>
                <p className="text-[11px] text-zinc-500 mt-1">Scores 1–10. Leave blank to skip.</p>
                <div className="space-y-3 mt-3">
                  <NumberInput label="Acting" testid="score-acting" value={scoreForm.acting_score} onChange={(v) => setScoreForm((s) => ({ ...s, acting_score: v }))} />
                  <NumberInput label="Vocal" testid="score-vocal" value={scoreForm.vocal_score} onChange={(v) => setScoreForm((s) => ({ ...s, vocal_score: v }))} />
                  <NumberInput label="Dance" testid="score-dance" value={scoreForm.dance_score} onChange={(v) => setScoreForm((s) => ({ ...s, dance_score: v }))} />
                </div>
                <div className="mt-3">
                  <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Comment</Label>
                  <Textarea data-testid="score-comment" rows={4} value={scoreForm.comment} onChange={(e) => setScoreForm((s) => ({ ...s, comment: e.target.value }))} className="bg-[#0A0A0A] border-white/10 mt-1" placeholder="Impressions, notes, callbacks…" />
                </div>
                <div className="mt-3">
                  <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Suggested roles</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <Input value={suggestQuery} onChange={(e) => setSuggestQuery(e.target.value)} placeholder="Type role & press add" className="bg-[#0A0A0A] border-white/10 h-9 text-sm" />
                    <button onClick={addSuggested} className="h-9 px-2 rounded border border-white/15 text-xs">Add</button>
                  </div>
                  {characters.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {characters.slice(0, 8).map((c) => (
                        <button key={c.id} onClick={() => setScoreForm((s) => s.suggested_roles.includes(c.name) ? s : ({ ...s, suggested_roles: [...s.suggested_roles, c.name] }))} className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-zinc-400 hover:text-white hover:bg-white/5">+ {c.name}</button>
                      ))}
                    </div>
                  )}
                  {scoreForm.suggested_roles.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {scoreForm.suggested_roles.map((r) => (
                        <span key={r} className="text-[10px] px-1.5 py-0.5 rounded-full bg-gold/10 text-gold border border-gold/30">{r}<button onClick={() => rmSuggested(r)} className="ml-1 text-gold/70 hover:text-gold">×</button></span>
                      ))}
                    </div>
                  )}
                </div>
                <button data-testid="save-score-btn" onClick={saveScore} style={goldBtn} className="mt-4 w-full h-10 rounded font-semibold text-sm hover:brightness-110">
                  {detail.my_score ? "Update your score" : "Save score"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cast dialog moved inline into DetailPanel above — no portal, no
          bubbled-onClose surprises. Keeping this comment as a landmark. */}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-1.5">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
function Field({ label, value, multiline }) {
  if (!value) return null;
  return (
    <div className="flex gap-3">
      <div className="w-32 shrink-0 text-[11px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`flex-1 text-zinc-200 ${multiline ? "whitespace-pre-wrap" : ""}`}>{value}</div>
    </div>
  );
}
function NumberInput({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-14 text-[11px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="flex-1 flex items-center gap-1">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
          <button key={n} onClick={() => onChange(value === n ? null : n)} data-testid={`${testid}-${n}`}
            className={`flex-1 h-8 text-[11px] font-mono rounded border transition-colors
              ${value === n ? "bg-gold text-black border-gold font-bold" : "border-white/10 text-zinc-400 hover:bg-white/5"}`}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Auditions() {
  const { user, production } = useAuth();
  // production_staff has the same reach as org_admin in the audition
  // module — the user asked for casting cycles to run without waiting
  // on an admin to add a round or finalise a callback.
  const isAdmin = user.role === "org_admin" || user.role === "production_staff" || user.role === "super_admin";
  const [items, setItems] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [slots, setSlots] = useState([]);
  const [roundFilter, setRoundFilter] = useState("all");   // "all" | round_id | "__none__"
  const [filter, setFilter] = useState("all");   // all | pending | cast | reject
  const [virtualOnly, setVirtualOnly] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizePreview, setFinalizePreview] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const [manageRoundsOpen, setManageRoundsOpen] = useState(false);
  const [slotsDialogOpen, setSlotsDialogOpen] = useState(false);
  const [judgingDialogOpen, setJudgingDialogOpen] = useState(false);
  const [fieldsDialogOpen, setFieldsDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [fields, setFields] = useState([]);
  const [newRoundName, setNewRoundName] = useState("");

  const load = useCallback(async () => {
    const [a, c, r, sl, cf] = await Promise.all([
      api.get("/auditions"),
      api.get("/characters"),
      api.get("/audition-rounds"),
      api.get("/audition-slots").catch(() => ({ data: [] })),
      api.get("/audition-fields").catch(() => ({ data: [] })),
    ]);
    setItems(a.data); setCharacters(c.data); setRounds(r.data);
    setSlots(sl.data || []); setFields(cf.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const signupUrl = useMemo(() => {
    if (!production?.enroll_code) return "";
    return `${window.location.origin}/audition/${production.enroll_code}`;
  }, [production]);

  const copyLink = () => {
    try { navigator.clipboard?.writeText(signupUrl); toast.success("Signup link copied"); }
    catch { toast.info(signupUrl); }
  };

  const filtered = useMemo(() => {
    const term = q.toLowerCase().trim();
    return items.filter((a) => {
      if (filter !== "all" && (a.status || "pending") !== filter) return false;
      if (virtualOnly && !a.is_virtual) return false;
      if (roundFilter === "__none__") { if (a.round_id) return false; }
      else if (roundFilter !== "all") { if (a.round_id !== roundFilter) return false; }
      if (!term) return true;
      return (a.name?.toLowerCase().includes(term) || a.email?.toLowerCase().includes(term) || (a.interested_roles || []).some((r) => r.toLowerCase().includes(term)));
    });
  }, [items, filter, roundFilter, q, virtualOnly]);

  const virtualCount = useMemo(() => items.filter((a) => a.is_virtual).length, [items]);

  const counts = useMemo(() => {
    const scope = items.filter((a) => {
      if (roundFilter === "__none__") return !a.round_id;
      if (roundFilter !== "all") return a.round_id === roundFilter;
      return true;
    });
    const c = { all: scope.length, pending: 0, cast: 0, reject: 0 };
    scope.forEach((a) => { c[a.status || "pending"] = (c[a.status || "pending"] || 0) + 1; });
    return c;
  }, [items, roundFilter]);

  const roundCounts = useMemo(() => {
    const c = { all: items.length, __none__: 0 };
    items.forEach((a) => {
      if (a.round_id) c[a.round_id] = (c[a.round_id] || 0) + 1;
      else c.__none__ += 1;
    });
    return c;
  }, [items]);

  const createRound = async () => {
    const n = newRoundName.trim();
    if (!n) return toast.error("Round name is required");
    try {
      await api.post("/audition-rounds", { name: n });
      setNewRoundName("");
      toast.success(`Round "${n}" created`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create round"); }
  };
  const renameRound = async (r) => {
    const name = window.prompt("Rename round", r.name);
    if (!name || name.trim() === r.name) return;
    try {
      await api.patch(`/audition-rounds/${r.id}`, { name: name.trim() });
      toast.success("Renamed");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not rename"); }
  };
  const deleteRound = async (r) => {
    if (!confirm(`Delete round "${r.name}"? Auditioners in this round will move to the next available round.`)) return;
    try {
      await api.delete(`/audition-rounds/${r.id}`);
      if (roundFilter === r.id) setRoundFilter("all");
      toast.success("Round removed");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete"); }
  };

  const openFinalize = async () => {
    try {
      const { data } = await api.get("/auditions/finalize-preview");
      setFinalizePreview(data); setFinalizeOpen(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load preview"); }
  };

  const runFinalize = async () => {
    setFinalizing(true);
    try {
      const { data } = await api.post("/auditions/finalize");
      toast.success(`Finalized · ${data.cast_promoted} cast, ${data.rejections_sent} notified`);
      setFinalizeOpen(false); setFinalizePreview(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Finalize failed"); }
    finally { setFinalizing(false); }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="auditions-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Casting</div>
          <h1 className="mt-1 font-display text-4xl tracking-tight font-light">Auditions</h1>
          <p className="mt-1 text-zinc-400 text-sm">Review, score, and cast auditioners for {production?.title}.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/audition-day"
            data-testid="open-audition-day-btn"
            className="h-9 px-3 rounded-md border border-gold/40 text-gold hover:bg-gold/10 text-sm inline-flex items-center gap-1.5"
          >
            <UserCheck className="w-3.5 h-3.5" /> Audition day board
          </Link>
          <button data-testid="copy-audition-link" onClick={copyLink} className="h-9 px-3 rounded-md border border-white/15 text-zinc-300 hover:bg-white/5 text-sm inline-flex items-center gap-1.5">
            <Copy className="w-3.5 h-3.5" /> Copy signup link
          </button>
          <a href={signupUrl} target="_blank" rel="noreferrer" className="h-9 px-3 rounded-md border border-white/15 text-zinc-300 hover:bg-white/5 text-sm inline-flex items-center gap-1.5">
            <ExternalLink className="w-3.5 h-3.5" /> Open form
          </a>
          <button data-testid="open-slots-btn" onClick={() => setSlotsDialogOpen(true)}
            className="h-9 px-3 rounded-md border border-white/15 text-zinc-300 hover:bg-white/5 text-sm inline-flex items-center gap-1.5">
            <CalendarClock className="w-3.5 h-3.5" /> Slots
          </button>
          {isAdmin && (
            <button data-testid="open-rounds-btn" onClick={() => setManageRoundsOpen(true)}
              className="h-9 px-3 rounded-md border border-white/15 text-zinc-300 hover:bg-white/5 text-sm inline-flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5" /> Rounds
            </button>
          )}
          <button data-testid="open-share-scores-btn" onClick={() => setShareDialogOpen(true)}
            className="h-9 px-3 rounded-md border border-gold/40 text-gold hover:bg-gold/10 text-sm inline-flex items-center gap-1.5">
            <Share2 className="w-3.5 h-3.5" /> Share scores
          </button>
          <button data-testid="open-analytics-btn" onClick={() => setAnalyticsOpen(true)}
            className="h-9 px-3 rounded-md border border-gold/40 text-gold hover:bg-gold/10 text-sm inline-flex items-center gap-1.5">
            <BarChart3 className="w-3.5 h-3.5" /> Analytics
          </button>
          {isAdmin && (
            <Link
              to="/email-templates"
              data-testid="open-email-templates-btn"
              className="h-9 px-3 rounded-md border border-white/15 text-zinc-300 hover:bg-white/5 text-sm inline-flex items-center gap-1.5"
            >
              <Mail className="w-3.5 h-3.5" /> Emails
            </Link>
          )}
          {isAdmin && (
            <button data-testid="open-fields-btn" onClick={() => setFieldsDialogOpen(true)}
              className="h-9 px-3 rounded-md border border-white/15 text-zinc-300 hover:bg-white/5 text-sm inline-flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Custom fields
            </button>
          )}
          {isAdmin && (
            <button data-testid="open-judging-btn" onClick={() => setJudgingDialogOpen(true)}
              className="h-9 px-3 rounded-md border border-gold/40 text-gold hover:bg-gold/10 text-sm inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Judges
            </button>
          )}
          {isAdmin && (
            <button data-testid="finalize-btn" onClick={openFinalize} style={goldBtn} className="h-9 px-3 rounded font-semibold text-sm inline-flex items-center gap-1.5 hover:brightness-110">
              <Trophy className="w-4 h-4" /> Finalize auditions
            </button>
          )}
        </div>
      </header>

      {/* Cast offer responses (Iter 57) — appears once at least one cast
          offer has been finalized. Hidden entirely for shows with zero
          offers so the page stays clean during first-round auditions. */}
      <AuditionResponsesPane productionId={production?.id} refreshKey={items.length} />

      {/* Rounds tabs */}
      <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-2 flex flex-wrap items-center gap-1" data-testid="rounds-bar">
        <button data-testid="round-tab-all" onClick={() => setRoundFilter("all")}
          className={`h-8 px-3 text-xs uppercase tracking-widest rounded transition-colors ${roundFilter === "all" ? "bg-gold text-black font-semibold" : "text-zinc-300 hover:bg-white/5"}`}>
          All <span className="opacity-70 ml-1 font-mono">{roundCounts.all}</span>
        </button>
        {rounds.map((r) => (
          <button key={r.id} data-testid={`round-tab-${r.id}`} onClick={() => setRoundFilter(r.id)}
            className={`h-8 px-3 text-xs uppercase tracking-widest rounded transition-colors inline-flex items-center gap-1
              ${roundFilter === r.id ? "bg-gold text-black font-semibold" : "text-zinc-300 hover:bg-white/5"}`}>
            <Layers className="w-3 h-3" /> {r.name} <span className="opacity-70 ml-1 font-mono">{roundCounts[r.id] || 0}</span>
          </button>
        ))}
        {roundCounts.__none__ > 0 && (
          <button data-testid="round-tab-none" onClick={() => setRoundFilter("__none__")}
            className={`h-8 px-3 text-xs uppercase tracking-widest rounded transition-colors ${roundFilter === "__none__" ? "bg-gold text-black font-semibold" : "text-zinc-500 hover:bg-white/5"}`}>
            Unassigned <span className="opacity-70 ml-1 font-mono">{roundCounts.__none__}</span>
          </button>
        )}
        {isAdmin && (
          <button data-testid="manage-rounds-btn" onClick={() => setManageRoundsOpen(true)}
            className="ml-auto h-8 px-3 text-xs uppercase tracking-widest rounded border border-white/10 text-zinc-300 hover:bg-white/5 inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Manage rounds
          </button>
        )}
      </div>

      {/* Virtual-only quick toggle — chip sits above the status stat grid so
          judges can restrict a batch review to remote submissions in one click. */}
      <div>
        <button
          data-testid="filter-virtual-only"
          onClick={() => setVirtualOnly(!virtualOnly)}
          className={`h-8 px-3 rounded-full border text-xs inline-flex items-center gap-1.5 transition-colors ${
            virtualOnly ? "bg-gold text-black border-gold font-semibold" : "border-white/15 text-zinc-300 hover:bg-white/5"
          }`}
          title={virtualOnly ? "Showing only virtual auditions" : "Toggle to see only virtual auditions"}
        >
          <Sparkles className="w-3.5 h-3.5" /> Virtual only <span className="font-mono opacity-70">{virtualCount}</span>
        </button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {["all", "pending", "cast", "reject"].map((k) => (
          <button
            key={k}
            data-testid={`filter-${k}`}
            onClick={() => setFilter(k)}
            className={`text-left rounded-lg border p-4 transition-colors
              ${filter === k ? "border-gold bg-gold/5" : "border-white/10 bg-[#121212] hover:border-white/20"}`}
          >
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              {k === "all" ? "Total" : STATUS_META[k]?.label}
            </div>
            <div className="mt-1 font-display text-3xl">{counts[k] || 0}</div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, role…" className="pl-9 bg-[#121212] border-white/10 h-10" />
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <EmptyState
          icon={UsersIcon}
          title={items.length === 0 ? "No auditions yet" : "No matches"}
          body={items.length === 0
            ? "Share the signup link with your auditioners. Their submissions will land here as cards you can score, filter by round, and cast."
            : "Try a different search term or clear filters to see all auditions."}
          cta={items.length === 0 && signupUrl ? (
            <button data-testid="copy-audition-link-empty" onClick={copyLink} style={goldBtn} className="h-9 px-3 rounded font-semibold text-sm inline-flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Copy signup link</button>
          ) : null}
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {filtered.map((a) => <AuditionCard key={a.id} a={a} onOpen={setSelected} />)}
        </div>
      )}

      {selected && <DetailPanel audition={selected} onClose={() => setSelected(null)} refresh={load} characters={characters} rounds={rounds} slots={slots} fields={fields} />}

      <AuditionSlotsDialog open={slotsDialogOpen} onOpenChange={(v) => { setSlotsDialogOpen(v); if (!v) load(); }} isAdmin={isAdmin} />

      <JudgingLinkDialog open={judgingDialogOpen} onOpenChange={setJudgingDialogOpen} isAdmin={isAdmin} />

      <AuditionFieldsDialog open={fieldsDialogOpen} onOpenChange={(v) => { setFieldsDialogOpen(v); if (!v) load(); }} isAdmin={isAdmin} />
      <ShareScoresDialog open={shareDialogOpen} onOpenChange={setShareDialogOpen} productionId={production?.id} />
      <AuditionAnalytics open={analyticsOpen} onOpenChange={setAnalyticsOpen} productionId={production?.id} productionTitle={production?.title} />      {/* Manage rounds dialog */}
      <Dialog open={manageRoundsOpen} onOpenChange={setManageRoundsOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md" data-testid="manage-rounds-dialog">
          <DialogHeader><DialogTitle className="font-display">Manage callback rounds</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-[11px] text-zinc-500">Create rounds like Initial · Callback · Dance call · Final, and move candidates between them from any audition profile. Scores are recorded per round.</div>
            <ul className="space-y-1 max-h-64 overflow-y-auto -mr-1 pr-1">
              {rounds.map((r) => (
                <li key={r.id} data-testid={`round-row-${r.id}`} className="flex items-center gap-2 rounded border border-white/10 bg-[#0A0A0A] px-2 py-1.5">
                  <Layers className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                  <span className="flex-1 text-sm">{r.name}</span>
                  <span className="text-[10px] text-zinc-500 font-mono">{roundCounts[r.id] || 0}</span>
                  <button onClick={() => renameRound(r)} className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white h-7 px-2 rounded border border-white/10">Rename</button>
                  <button data-testid={`del-round-${r.id}`} onClick={() => deleteRound(r)} className="text-[10px] uppercase tracking-widest text-red-400 hover:text-red-300 h-7 px-2 rounded border border-red-500/30"><Trash2 className="w-3 h-3" /></button>
                </li>
              ))}
              {rounds.length === 0 && <li className="text-xs text-zinc-500">No rounds yet.</li>}
            </ul>
            <div className="flex items-center gap-2 pt-2 border-t border-white/10">
              <Input data-testid="new-round-name" value={newRoundName} onChange={(e) => setNewRoundName(e.target.value)} placeholder='e.g. "Callback"' className="bg-[#0A0A0A] border-white/10 h-9" onKeyDown={(e) => { if (e.key === "Enter") createRound(); }} />
              <button data-testid="add-round-btn" onClick={createRound} style={goldBtn} className="h-9 px-3 rounded font-semibold text-sm inline-flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Finalize dialog */}
      <Dialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg max-h-[85vh] flex flex-col overflow-hidden" data-testid="finalize-dialog">
          <DialogHeader className="shrink-0"><DialogTitle className="font-display">Finalize auditions</DialogTitle></DialogHeader>
          {finalizePreview && (
            <div className="space-y-4 text-sm flex-1 overflow-y-auto min-h-0 pr-1 -mr-1">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2 sticky top-0 bg-[#121212] py-1">Will be cast ({finalizePreview.cast.length})</div>
                {finalizePreview.cast.length === 0 ? <div className="text-zinc-500 text-xs">Nobody marked as Cast yet.</div> : (
                  <ul className="space-y-1">
                    {finalizePreview.cast.map((c) => (
                      <li key={c.id} className="flex justify-between border-b border-white/5 pb-1">
                        <span>{c.name}</span><span className="text-zinc-400">{c.cast_character || "—"}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-red-400 mb-2 sticky top-0 bg-[#121212] py-1">Will be sent rejection email ({finalizePreview.reject.length})</div>
                {finalizePreview.reject.length === 0 ? <div className="text-zinc-500 text-xs">No rejections queued.</div> : (
                  <ul className="space-y-1">
                    {finalizePreview.reject.map((c) => (
                      <li key={c.id} className="text-zinc-400">{c.name} <span className="text-[10px] text-zinc-500">· {c.email}</span></li>
                    ))}
                  </ul>
                )}
              </div>
              {finalizePreview.pending_count > 0 && (
                <div className="text-[11px] text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded px-2 py-1.5">
                  {finalizePreview.pending_count} auditioner{finalizePreview.pending_count === 1 ? "" : "s"} still pending — they will not be affected.
                </div>
              )}
              <div className="text-[11px] text-zinc-500">Cast decision emails and rejection notifications will be sent to each auditioner&apos;s email address.</div>
            </div>
          )}
          <DialogFooter className="shrink-0 border-t border-white/10 pt-3 mt-1 -mx-6 px-6 bg-[#121212]">
            <button onClick={() => setFinalizeOpen(false)} className="h-9 px-3 rounded border border-white/15 text-zinc-300 hover:bg-white/5 text-sm">Cancel</button>
            <button data-testid="confirm-finalize-btn" onClick={runFinalize} disabled={finalizing || !finalizePreview || (finalizePreview.cast.length === 0 && finalizePreview.reject.length === 0)}
              style={goldBtn} className="h-9 px-3 rounded font-semibold text-sm inline-flex items-center gap-1.5 disabled:opacity-50">
              <Send className="w-3.5 h-3.5" /> {finalizing ? "Finalizing…" : "Confirm & finalize"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
